/**
 * X (Twitter) Ads Tracking Utility
 * Implements dual tracking: Browser Pixel (twq) + Server-side Conversion API
 * with proper deduplication via shared event_id, PII hashing (server-side),
 * and twclid capture from URL params.
 *
 * Mirrors the tiktokTracking module so every call site can fire both TikTok
 * and X events with the same semantic payload. No new instrumentation surface.
 */

// X standard event types — must match what's configured in Events Manager.
// Custom events use the CustomEvent type + a configured event_id.
export type XEventType =
  | "PageView"
  | "ContentView"
  | "Signup"
  | "Lead"
  | "Subscribe"
  | "InitiateCheckout"
  | "AddToCart"
  | "Purchase"
  | "Search"
  | "Download"
  | "CustomEvent";

interface XUserData {
  email?: string;
  phone?: string;
  externalId?: string;
}

interface XEventOptions {
  user?: XUserData;
  properties?: {
    value?: number;
    currency?: string;
    content_ids?: string[];
    content_type?: string;
    content_name?: string;
    num_items?: number;
    [k: string]: unknown;
  };
  /**
   * When provided, uses tw-<PIXEL_ID>-<eventTagId> as the pixel event key.
   * Get eventTagId from ads.x.com → Events Manager → your event → "Event ID".
   * Optional — if not set, the pixel event is skipped (server CAPI still fires).
   */
  eventTagId?: string;
}

declare global {
  interface Window {
    twq?: (
      command: string,
      arg1?: string | Record<string, unknown>,
      arg2?: Record<string, unknown>,
    ) => void;
  }
}

const EDGE_FUNCTION_URL =
  "https://txoojazdivnmgstunpic.supabase.co/functions/v1/x-ads-events";
const isDev = import.meta.env.DEV;

// PIXEL_ID and EVENT_TAG_IDS come from Vite env vars. Set:
//   VITE_X_PIXEL_ID=xxxxx
//   VITE_X_EVENT_TAG_SIGNUP=xxxxx
//   VITE_X_EVENT_TAG_SUBSCRIBE=xxxxx
//   VITE_X_EVENT_TAG_PURCHASE=xxxxx
//   VITE_X_EVENT_TAG_LEAD=xxxxx
// (retrieve from ads.x.com → Events Manager per event)
const PIXEL_ID = import.meta.env.VITE_X_PIXEL_ID as string | undefined;

const EVENT_TAG_IDS: Partial<Record<XEventType, string>> = {
  Signup: import.meta.env.VITE_X_EVENT_TAG_SIGNUP as string | undefined,
  Subscribe: import.meta.env.VITE_X_EVENT_TAG_SUBSCRIBE as string | undefined,
  Purchase: import.meta.env.VITE_X_EVENT_TAG_PURCHASE as string | undefined,
  Lead: import.meta.env.VITE_X_EVENT_TAG_LEAD as string | undefined,
  InitiateCheckout: import.meta.env.VITE_X_EVENT_TAG_CHECKOUT as
    | string
    | undefined,
};

/**
 * Capture twclid (X click ID) from URL params and store in localStorage.
 * Persists across sessions so downstream conversions carry the click ID.
 */
export function captureTwclid(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const twclid = params.get("twclid");
    if (twclid) {
      localStorage.setItem("twclid", twclid);
      if (isDev) console.log("[X] Captured twclid:", twclid);
    }
  } catch (err) {
    if (isDev) console.error("[X] Error capturing twclid:", err);
  }
}

export function getTwclid(): string | null {
  try {
    return localStorage.getItem("twclid");
  } catch {
    return null;
  }
}

function generateEventId(): string {
  return crypto.randomUUID();
}

/**
 * Identify the current user in the X pixel. Safe to call multiple times.
 * X's pixel hashes email/phone client-side before shipping.
 */
export function identifyXUser(user: XUserData): void {
  try {
    if (typeof window === "undefined" || !window.twq || !PIXEL_ID) return;
    const payload: Record<string, string> = {};
    if (user.email) payload.email_address = user.email.toLowerCase().trim();
    if (user.phone) payload.phone_number = user.phone;
    if (user.externalId) payload.external_id = user.externalId;
    if (Object.keys(payload).length === 0) return;
    // X's identify command: pass config with user data.
    window.twq("config", PIXEL_ID, payload);
    if (isDev) console.log("[X Pixel] Identified user:", Object.keys(payload));
  } catch (err) {
    if (isDev) console.warn("[X Pixel] identify failed:", err);
  }
}

function firePixelEvent(
  event: XEventType,
  eventId: string,
  eventTagId: string | undefined,
  properties?: XEventOptions["properties"],
): void {
  try {
    if (typeof window === "undefined" || !window.twq || !PIXEL_ID) return;
    if (!eventTagId) {
      // Without an event tag ID we can't fire — the pixel needs a specific
      // tw-<pixel>-<event> conversion key. Server CAPI still runs.
      if (isDev) {
        console.log(
          "[X Pixel] Skipping pixel event (no eventTagId for " + event + ")",
        );
      }
      return;
    }
    const tag = `tw-${PIXEL_ID}-${eventTagId}`;
    const eventPayload: Record<string, unknown> = {
      conversion_id: eventId, // Dedup key
    };
    if (properties?.value !== undefined) {
      eventPayload.value = properties.value;
    }
    if (properties?.currency) eventPayload.currency = properties.currency;
    if (properties?.content_ids) {
      eventPayload.content_ids = properties.content_ids;
    }
    if (properties?.content_type) {
      eventPayload.content_type = properties.content_type;
    }
    if (properties?.num_items !== undefined) {
      eventPayload.num_items = properties.num_items;
    }
    window.twq("event", tag, eventPayload);
    if (isDev) console.log("[X Pixel] Fired:", event, tag, eventPayload);
  } catch (err) {
    if (isDev) console.error("[X Pixel] Error firing event:", err);
  }
}

async function sendServerEvent(
  event: XEventType,
  eventId: string,
  url: string,
  options?: XEventOptions,
): Promise<void> {
  try {
    const payload = {
      event,
      eventId,
      url,
      user: options?.user,
      properties: options?.properties,
      twclid: getTwclid(),
      userAgent: navigator.userAgent,
    };

    if (isDev) console.log("[X CAPI] Sending:", payload);

    await fetch(EDGE_FUNCTION_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
  } catch (err) {
    if (isDev) console.error("[X CAPI] Error:", err);
    // Silent fail — tracking must never break the app.
  }
}

/**
 * Main entry point. Fires both browser pixel (if event tag configured)
 * and server-side CAPI (always). Same event_id for dedup.
 */
export async function trackXAdsEvent(
  event: XEventType,
  options?: XEventOptions,
): Promise<void> {
  const eventId = generateEventId();
  const url = typeof window !== "undefined" ? window.location.href : "";

  const eventTagId = options?.eventTagId ?? EVENT_TAG_IDS[event];
  firePixelEvent(event, eventId, eventTagId, options?.properties);
  sendServerEvent(event, eventId, url, options).catch(() => {});
}
